import { Component, Input, OnInit, output, input } from '@angular/core';
import { Button } from "./Button";
import { Integration } from "./Integration";
import { CombiPackage, UserCombi, UserCombiPackage } from "../model";
import { TranslateService } from "@ngx-translate/core";
import { SharedModule } from "../modules/shared/shared.module";
import { logRunNow } from "../util/analytics.util";
import { MessageService } from "../modules/shared/message/message.service";
import { UserCombiService } from "../services/usercombi/usercombi.service";

// TODO move to own file
export interface AppIntegrationFromTo {
  apiInternalKey: string;
  apiName: string;
  apiNameNormalized: string;
  userApiName?: string;
}

export class AppIntegration {
  internalKey: string;
  description: string;
  trialMessage?: string;
  message: string;
  status: "installing" | "active" | "trial-ended" | "trial-running" | "error";
  errorType?: "userapi_disconnected" | "awaiting_payment";
  from: AppIntegrationFromTo;
  to: AppIntegrationFromTo;
  userCombiPackage?: UserCombiPackage;
  specialRecommendation?: boolean;
  historyProgress?: { processed: number; total: number };

  constructor(internalKey: string) {
    this.internalKey = internalKey;
    this.from = <any>{};
    this.to = <any>{};
  }

  static fromCombiPackage(combiPackage: CombiPackage, translate: TranslateService): AppIntegration {
    const appIntegration = new AppIntegration(combiPackage.internalKey);
    appIntegration.specialRecommendation = combiPackage.specialRecommendation;

    appIntegration.description = combiPackage.description;
    if (!appIntegration.description) {
      const key = "PAGE_APPSTORE_PACKAGE_TITLE_" + combiPackage.internalKey;
      const params = {
        from: combiPackage.withAPI.name,
        to: combiPackage.otherApiName
      };
      appIntegration.description = translate.instant(key, params);
      if (appIntegration.description.indexOf(key) > -1) {
        appIntegration.description = translate.instant("PAGE_APPSTORE_PACKAGE_TITLE", params);
      }
    }

    appIntegration.from.apiName = combiPackage.withAPI.name;
    appIntegration.from.apiNameNormalized = combiPackage.withAPI.nameNormalized;
    appIntegration.from.apiInternalKey = combiPackage.withAPI.internalKey;

    appIntegration.to.apiName = combiPackage.otherApiName;
    appIntegration.to.apiNameNormalized = combiPackage.otherApiNameNormalized;

    return appIntegration;
  }

  static fromUserCombiPackage(userCombiPackage: UserCombiPackage, translate: TranslateService): AppIntegration {
    const appIntegration = new AppIntegration(userCombiPackage.packageinternalkey);
    appIntegration.userCombiPackage = userCombiPackage;

    if (userCombiPackage.status === "INSTALLING") {
      appIntegration.status = "installing";
      appIntegration.message = translate.instant("PAGE_DASHBOARD_PACKAGE_INSTALLING");
    } else if (userCombiPackage.status === "AWAITING_PAYMENT") {
      appIntegration.status = "error";
      appIntegration.errorType = "awaiting_payment";
      appIntegration.message = translate.instant("PAGE_DASHBOARD_PACKAGE_AWAITING_PAYMENT");
    } else if (userCombiPackage.fromUserAPI && userCombiPackage.toUserAPI && (!userCombiPackage.fromUserAPI.connected || !userCombiPackage.toUserAPI.connected)) {
      appIntegration.status = "error";
      appIntegration.errorType = "userapi_disconnected";
      appIntegration.message = translate.instant("PAGE_DASHBOARD_USERAPI_DISCONNECTED", {api: !userCombiPackage.fromUserAPI.connected ? userCombiPackage.fromUserAPI.api.name : userCombiPackage.toUserAPI.api.name});
    } else if (userCombiPackage.status === "TRIAL") {
      appIntegration.status = "trial-running";
      if (userCombiPackage.combideskAppStorePackage && userCombiPackage.combiOrder) {
        appIntegration.trialMessage = translate.instant(userCombiPackage.combiOrder.daysLeft <= 14 ? (userCombiPackage.combiOrder.daysLeft === 1 ? 'PAGE_DASHBOARD_DAY_LEFT_FREE' : 'PAGE_DASHBOARD_DAYS_LEFT_FREE') : 'PAGE_DASHBOARD_DAYS_LAST_ANOTHER', {daysleft: userCombiPackage.combiOrder.daysLeft});
      }
    } else if (userCombiPackage.status === "TRIAL_EXPIRED") {
      appIntegration.status = "trial-ended";
      if (userCombiPackage.combideskAppStorePackage) {
        appIntegration.trialMessage = translate.instant("PAGE_DASHBOARD_TRIAL_EXPIRED");
      }
    } else {
      appIntegration.status = "active";
    }

    if (appIntegration.status !== "error") {
      const isRunning = userCombiPackage.userCombis.some(userCombi => userCombi.running);
      const scheduledUserCombis = userCombiPackage.userCombis.filter(userCombi => userCombi.combiType === "SCHEDULED");
      const hasScheduledUserCombis = scheduledUserCombis.length > 0;

      if (hasScheduledUserCombis && (!userCombiPackage.canBePaused || userCombiPackage.active)) {
        const didRun = scheduledUserCombis.filter(userCombi => userCombi.lastRunDate).length > 0;
        const catchingUp = scheduledUserCombis.find(userCombi => userCombi.historyTotalItems > 0 && userCombi.historyProcessedItems !== undefined);
        if (isRunning) {
          appIntegration.message = translate.instant("PAGE_DASHBOARD_USERCOMBI_RUNNING");
        } else if (!didRun) {
          const intervalMinutesCombis = scheduledUserCombis.filter(userCombi => userCombi.intervalMinutes);
          const hasIntervalMinutesCombisOnly = scheduledUserCombis.length === intervalMinutesCombis.length;
          if (hasIntervalMinutesCombisOnly) {
            const hours = ((intervalMinutesCombis[0].intervalMinutes / 60) < 10 ? "0" : "") + intervalMinutesCombis[0].intervalMinutes / 60;
            const minutes = ((intervalMinutesCombis[0].intervalMinutes % 60) < 10 ? "0" : "") + intervalMinutesCombis[0].intervalMinutes % 60;
            // note that this is our timezone.. which is often, but potentially not always correct
            appIntegration.message = translate.instant("PAGE_DASHBOARD_USERCOMBI_WILL_RUN_AT", { hours, minutes, AMPM: intervalMinutesCombis[0].intervalMinutes <= 720 ? "AM" : "PM" });
          } else {
            appIntegration.message = translate.instant("PAGE_DASHBOARD_USERCOMBI_WILL_RUN");
          }
        } else {
          const lastRunDateMinutesAgo = Math.min(...userCombiPackage.userCombis.map(userCombi => userCombi.lastRunDateMinutesAgo));
          const lastRunDateHoursAgo = Math.round(lastRunDateMinutesAgo / 60);
          const lastRunDateDaysAgo = Math.round(lastRunDateHoursAgo / 24);
          if (lastRunDateDaysAgo > 2) {
            appIntegration.message = translate.instant("PAGE_DASHBOARD_USERCOMBIPACKAGE_LASTRUN") + " " +
                translate.instant("PAGE_DASHBOARD_USERCOMBI_LASTRUN_DAYS", {daysAgo: lastRunDateDaysAgo});
          } else if (lastRunDateHoursAgo > 3) {
            appIntegration.message = translate.instant("PAGE_DASHBOARD_USERCOMBIPACKAGE_LASTRUN") + " " +
                translate.instant("PAGE_DASHBOARD_USERCOMBI_LASTRUN_HOURS", {hoursAgo: lastRunDateHoursAgo});
          } else {
            appIntegration.message = translate.instant("PAGE_DASHBOARD_USERCOMBIPACKAGE_LASTRUN") + " " +
                translate.instant("PAGE_DASHBOARD_USERCOMBI_LASTRUN_MINUTES", {minutesAgo: lastRunDateMinutesAgo});
          }
        }
        if (catchingUp) {
          appIntegration.historyProgress = {
            processed: catchingUp.historyProcessedItems,
            total: catchingUp.historyTotalItems
          };
        }
      }
    }

    const fromAPI = userCombiPackage.fromAPI ? userCombiPackage.fromAPI : userCombiPackage.userCombis[0].fromAPI;
    const toAPI = userCombiPackage.toAPI ? userCombiPackage.toAPI : userCombiPackage.userCombis[0].toAPI;

    appIntegration.from.apiName = fromAPI.api.name;
    appIntegration.from.apiNameNormalized = fromAPI.api.nameNormalized;
    appIntegration.from.apiInternalKey = fromAPI.api.internalKey;
    appIntegration.from.userApiName = userCombiPackage.fromUserAPI?.name;

    appIntegration.to.apiName = toAPI.api.name;
    appIntegration.to.apiNameNormalized = toAPI.api.nameNormalized;
    appIntegration.to.apiInternalKey = toAPI.api.internalKey;
    appIntegration.to.userApiName = userCombiPackage.toUserAPI?.name;

    return appIntegration;
  }
}

@Component({
  selector: 'DashboardIntegrations',
  imports: [Button, Integration, SharedModule],
  template: `
    <div>
      @if (label) {
        <div class="flex flex-wrap gap-y-2 gap-x-4 items-center justify-between mb-7">
          <h2 class="mb-0">{{ label }}</h2>
        </div>
      }

      @if (!integrations()) {
        <app-loading></app-loading>
      }

      <div [class]="integrationsWrapperStyleClass">

        @for (integration of integrations(); track integration.internalKey) {
          <Integration
              [firstApp]="integration.from"
              [secondApp]="integration.to"
              [integration]="integration"
              [type]="view"
              [showHistoryButton]="showHistoryButton()"
              [hasClickHandler]="hasClickHandler()"
              (clicked)="onClicked(integration.internalKey)"
              (orderButtonClicked)="onOrderButtonClicked(integration.userCombiPackage)"
              (runClicked)="onRunClicked($event)"
              (historyClicked)="onHistoryClicked($event)"
              (statisticsClicked)="onStatisticsClicked(integration.userCombiPackage)"
              (calculateQueueSizeClicked)="onCalculateQueueSizeClicked($event)"
              (abandonedCartStatisticsClicked)="onAbandonedCartStatisticsClicked(integration.userCombiPackage)"
              (editClicked)="onEditClicked(integration.userCombiPackage)"
              (deleteClicked)="onDeleteClicked(integration.userCombiPackage)"
              (restoreClicked)="onRestoreClicked(integration.userCombiPackage)"
              (deeplinkClicked)="onDeeplinkClicked($event, integration.userCombiPackage)"
              (configCheckClicked)="onConfigCheckClicked(integration.userCombiPackage)"
              class="min-w-0 h-full"
          ></Integration>
        }

        @if (showAddButton() && integrations()) {
          <div class="!border-t-0">
            <div
                class="
              border-gray-200
              border-2
              border-dashed
              rounded-3xl
              flex
              items-center
              justify-center
              {{
              view === 'grid'
                ? 'min-h-[200px] lg:min-h-[300px]'
                : 'min-h-[120px] mt-4'
            }}
            "
            >
              <Button color="red" size="small" [href]="'/' + ('BREADCRUMBS_INTEGRATIONS_ROUTE' | translate)">
                + {{ 'HEADER_MENU_ITEM_ADD_COMBI' | translate }}
              </Button>
            </div>
          </div>
        }
      </div>
    </div>
  `
})
export class DashboardIntegrations implements OnInit {
  @Input() label = '';
  readonly shopify = input(false);
  readonly showAddButton = input(true);
  readonly showHistoryButton = input(true);
  readonly showStatus = input(true);
  readonly storageKey = input('');
  @Input() view = 'grid';
  readonly columns = input<2 | 3>(2);
  readonly hasClickHandler = input(false);
  readonly integrations = input<Array<AppIntegration>>();
  runCompleted = output();

  integrationsWrapperStyleClass: string;

  // TODO once the old dashboard stuff has been removed, we can inline the code triggered by these emitters here
  clicked = output<string | undefined>();
  orderButtonClicked = output<UserCombiPackage>();
  historyClicked = output<UserCombi>();
  statisticsClicked = output<UserCombiPackage>();
  abandonedCartStatisticsClicked = output<UserCombiPackage>();
  editClicked = output<UserCombiPackage>();
  deleteClicked = output<UserCombiPackage>();
  restoreClicked = output<UserCombiPackage>();
  deeplinkClicked = output<UserCombiPackage>();
  configCheckClicked = output<UserCombiPackage>();

  constructor(private translate: TranslateService,
              private messageService: MessageService,
              private userCombiService: UserCombiService) {
  }

  async ngOnInit() {
    this.view = localStorage.getItem(this.storageKey()) || this.view;
    const gridColsClass = this.columns() === 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2';
    this.integrationsWrapperStyleClass = this.view === 'grid' ? (this.shopify() ? 'gap-5 grid' : `gap-5 grid ${gridColsClass}`) : 'flex flex-col divide-y border-t';
  }

  onClicked(packageInternalKey: string): void {
    this.clicked.emit(packageInternalKey);
  }

  onOrderButtonClicked(userCombiPackage: UserCombiPackage): void {
    if (this.orderButtonClicked) {
      this.orderButtonClicked.emit(userCombiPackage);
    }
  }

  async onCalculateQueueSizeClicked(userCombi: UserCombi) {
    const queue = await this.userCombiService.calculateQueueSize(userCombi.id);
    if (queue !== undefined) {
      this.messageService.showSuccessMessage(this.translate.instant("PAGE_DASHBOARD_ALERT_QUEUE_SIZE", {queue}));
    }
  }

  async onRunClicked(userCombi: UserCombi) {
    logRunNow(userCombi);
    this.messageService.showSuccessMessage(this.translate.instant('PAGE_DASHBOARD_ALERT_EXECUTING_COMBI'));
    await this.userCombiService.run(userCombi.id);
    this.runCompleted.emit();
  }

  onHistoryClicked(userCombi: UserCombi) {
    this.historyClicked.emit(userCombi);
  }

  onStatisticsClicked(userCombiPackage: UserCombiPackage) {
    this.statisticsClicked.emit(userCombiPackage);
  }

  onAbandonedCartStatisticsClicked(userCombiPackage: UserCombiPackage) {
    this.abandonedCartStatisticsClicked.emit(userCombiPackage);
  }

  onEditClicked(userCombiPackage: UserCombiPackage) {
    this.editClicked.emit(userCombiPackage);
  }

  onDeleteClicked(userCombiPackage: UserCombiPackage) {
    this.deleteClicked.emit(userCombiPackage);
  }

  onRestoreClicked(userCombiPackage: UserCombiPackage) {
    this.restoreClicked.emit(userCombiPackage);
  }

  onDeeplinkClicked(stepName: string, userCombiPackage: UserCombiPackage) {
    if (userCombiPackage.editPackageConfigurationUrl != undefined && stepName !== undefined) {
      userCombiPackage.editPackageConfigurationUrl = userCombiPackage.editPackageConfigurationUrl + '&jumpToStep=' + stepName;
    }
    this.deeplinkClicked.emit(userCombiPackage);
  }

  onConfigCheckClicked(userCombiPackage: UserCombiPackage) {
    this.configCheckClicked.emit(userCombiPackage);
  }
}
